/**
 * Bearer 토큰 저장소.
 *
 * Flutter bridge `getToken` 응답을 메모리에 보관 → axios 인터셉터에서 꺼내
 * `Authorization: Bearer ${token}` 헤더로 자동 주입.
 *
 * 비회원은 token이 없는 상태로 진행 — 광역권역 조회는 시스템 인증(x-api-key)으로 동작,
 * 사용자 API(waitlist, order/availability)는 401로 실패 (애초에 비회원이 도달 X).
 *
 * 외부 store 패턴 (subscribe + getSnapshot) — useSyncExternalStore 와 결합해 컴포넌트가
 * token 변경(예: 회원가입 후 setToken)을 즉시 인지하고 re-render. 사용처: hooks/useHasToken.
 */

let token: string | null = null
const listeners = new Set<() => void>()

export const setToken = (value: string): void => {
  token = value
  listeners.forEach((listener) => listener())
}

export const getToken = (): string | null => token

/** 토큰 보유 여부 — 함수 호출 시점 기준. React 컴포넌트는 useHasToken hook 사용 */
export const hasToken = (): boolean => token !== null && token !== ''

/**
 * 외부 store subscription — useSyncExternalStore 의 첫 인자.
 *
 * setToken 호출 시 등록된 listener 가 모두 호출되어 React 가 snapshot 을 다시 읽고 re-render.
 */
export const subscribeToken = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * 현재 token snapshot — useSyncExternalStore 의 두번째 인자.
 *
 * primitive(string|null) 라 reference equality 안전 (매 호출마다 같은 값이면 동일 참조).
 */
export const getTokenSnapshot = (): string | null => token
